import { supabase } from '@/integrations/supabase/client';
import { getCurrentUserProfileId } from '../users';
import type { SendNotificationRequest, SendNotificationResponse } from '@/types/notifications';

// Create and schedule a notification campaign
export const scheduleNotificationCampaign = async (
  request: SendNotificationRequest
): Promise<SendNotificationResponse> => {
  try {
    if (!request.scheduled_for) {
      throw new Error('scheduled_for is required for scheduled campaigns');
    }

    if (!request.user_group_id && (!request.user_ids || request.user_ids.length === 0)) {
      throw new Error('Either user_group_id or user_ids must be provided for scheduled campaigns');
    }

    if (!request.user_group_id) {
      throw new Error('Scheduled campaigns currently only support user groups. For individual users, please use immediate sending.');
    }

    // Template is optional — resolve title for campaign display
    let template: { id: number; name: string; title_template: string; body_template: string } | null = null;

    if (request.template_id) {
      const { data: templateData, error: templateError } = await supabase
        .from('notification_templates')
        .select('*')
        .eq('id', request.template_id)
        .single();

      if (templateError || !templateData) {
        throw new Error('Template not found');
      }
      template = templateData;
    } else if (!request.title || !request.body) {
      throw new Error('Either template_id or both title and body must be provided');
    }

    const currentUserProfileId = await getCurrentUserProfileId();

    const campaignTitle = template
      ? `Scheduled Campaign - ${template.name}`
      : `Scheduled Campaign - ${request.title}`;

    const campaignMessage = template
      ? `Scheduled notification campaign using template: ${template.name}`
      : JSON.stringify({ title: request.title, body: request.body });

    const campaignData = {
      title: campaignTitle,
      message: campaignMessage,
      type: 'notification',
      status: 'scheduled',
      template_id: template?.id || null,
      group_id: request.user_group_id,
      scheduled_at: request.scheduled_for,
      created_by: currentUserProfileId,
    };

    console.log('Creating scheduled campaign with data:', campaignData);

    const { data: campaign, error: campaignError } = await supabase
      .from('group_campaigns')
      .insert(campaignData)
      .select()
      .single();

    if (campaignError) throw campaignError;

    return {
      success: true,
      message: 'Campaign scheduled successfully',
      campaign_id: campaign.id,
    };

  } catch (error) {
    console.error('Error scheduling notification campaign:', error);
    throw error;
  }
};

// Process scheduled campaigns
export const processScheduledCampaigns = async (): Promise<{ success: boolean; message: string }> => {
  try {
    const { data, error } = await supabase.functions.invoke('process-scheduled-campaigns', {
      body: { process_all: true },
    });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error processing scheduled campaigns:', error);
    throw error;
  }
};
